import { useCallback, useEffect, useRef, useState } from "react";
import BootstrapModal from "bootstrap/js/dist/modal";

interface UseModalResult {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
  instance: BootstrapModal | null;
}

export const useModal = (
  modalId: string,
  options?: Record<any, any>
): UseModalResult => {
  const modalInstanceRef = useRef<BootstrapModal | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const getElement = useCallback(() => {
    if (typeof document === "undefined") return null;
    return document.getElementById(modalId);
  }, [modalId]);

  const ensureInstance = useCallback(() => {
    if (typeof window === "undefined") return null;

    const element = getElement();
    if (!element) return null;

    // Always getOrCreateInstance from Bootstrap
    const instance = BootstrapModal.getOrCreateInstance(element, options);
    modalInstanceRef.current = instance;
    return instance;
  }, [getElement, options]);

  useEffect(() => {
    const element = getElement();
    if (!element) return;

    const instance = BootstrapModal.getOrCreateInstance(element, options);
    modalInstanceRef.current = instance;

    const handleShown = () => setIsOpen(true);
    const handleHidden = () => setIsOpen(false);

    element.addEventListener("shown.bs.modal", handleShown);
    element.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      element.removeEventListener("shown.bs.modal", handleShown);
      element.removeEventListener("hidden.bs.modal", handleHidden);

      if (modalInstanceRef.current === instance) {
        modalInstanceRef.current = null;
      }
    };
  }, [getElement, options]);

  const open = useCallback(() => {
    ensureInstance()?.show();
  }, [ensureInstance]);

  const close = useCallback(() => {
    ensureInstance()?.hide();
  }, [ensureInstance]);

  const toggle = useCallback(() => {
    ensureInstance()?.toggle();
  }, [ensureInstance]);

  return {
    open,
    close,
    toggle,
    isOpen,
    instance: modalInstanceRef.current,
  };
};
